<template>
  <div>
    <p v-if="needsLogin" class="rb-ob-status" role="status">{{ $t('intake.portal.needsLogin') }}</p>
    <p v-else-if="loading" class="rb-ob-status" role="status">{{ $t('intake.portal.loading') }}</p>
    <template v-else-if="request">
      <section class="detail-card">
        <p><b>{{ $t('intake.portal.requestDetail_topic') }}:</b> {{ topicLabel }}</p>
        <p>
          <b>{{ $t('intake.portal.requestDetail_status') }}:</b>
          <span :class="['status', statusTone]">{{ statusLabel }}</span>
        </p>
        <p><b>{{ $t('intake.portal.requestDetail_last') }}:</b> {{ lastUpdateLabel }}</p>
      </section>
      <div class="chat-thread" :aria-label="$t('intake.portal.chatThreadAria')">
        <article v-for="m in messages" :key="m.id" class="chat-bubble" :class="m.actor_type === 'CLIENT' ? 'is-client' : 'is-therapist'">
          <header><b>{{ m.actor_type === 'CLIENT' ? $t('intake.portal.chatYou') : $t('intake.portal.requestDetail_reply') }}</b><time>{{ formatWhen(m.created_at) }}</time></header>
          <p>{{ m.body }}</p>
        </article>
      </div>
      <form v-if="canReply" class="chat-composer" @submit.prevent="send">
        <textarea v-model="draft" rows="2" :disabled="sending" :placeholder="$t('intake.portal.requestReplyPlaceholder')"></textarea>
        <div class="chat-composer-bar">
          <small>{{ $t('intake.portal.supportMessengerNote') }}</small>
          <button class="btn btn-primary btn-sm" type="submit" :disabled="sending">{{ sending ? $t('intake.portal.loading') : $t('intake.portal.chatSend') }}</button>
        </div>
      </form>
      <p v-else class="rb-ob-status" role="status">{{ $t('intake.portal.supportReplyClosed') }}</p>
    </template>
    <p class="rb-ob-status" role="status" aria-live="polite">{{ formStatus }}</p>
    <div class="actions"><router-link class="btn btn-primary" :to="{name:'portalSupport',params:{lang}}">{{ $t('intake.portal.requestDetailBack') }}</router-link></div>
  </div>
</template>

<script>
import {
  fetchSupportRequest,
  formatSupportWhen,
  hasAuthToken,
  sendSupportRequestMessage,
  supportStatusI18nKey,
  supportStatusTone,
  supportTopicI18nKey,
} from '@/intake/portalApi'

export default {
  name: 'PortalRequestDetailStep',
  data() { return {loading:true,sending:false,needsLogin:false,request:null,draft:'',formStatus:'',generation:0} },
  computed: {
    lang() { return this.$route.params.lang || 'fa' },
    messages() { return (this.request && Array.isArray(this.request.messages)) ? this.request.messages : [] },
    requestId() { return String(this.$route.query.id || '').trim() },
    canReply() { return !this.loading && !!this.request && String(this.request.id) === this.requestId && this.request.status === 'OPEN' },
    statusLabel() {
      const key = supportStatusI18nKey(this.request && (this.request.simple_status || this.request.status))
      const value = this.$t(key)
      return value === key ? (this.request && this.request.status) : value
    },
    statusTone() {
      return supportStatusTone(this.request && (this.request.simple_status || this.request.status))
    },
    lastUpdateLabel() {
      return formatSupportWhen(this.request && this.request.last_update_at, this.lang) || '—'
    },
    topicLabel() {
      const key = supportTopicI18nKey(this.request && this.request.topic)
      const value = this.$t(key)
      return value === key ? (this.request && this.request.topic) : value
    },
  },
  mounted() { this.bootstrap() },
  watch: {'$route.query.id'() { return this.bootstrap() }},
  beforeDestroy() { this.generation += 1 },
  methods: {
    formatWhen(iso) {
      return formatSupportWhen(iso, this.lang) || '—'
    },
    bootstrap() {
      const generation = ++this.generation
      this.loading = true
      this.request = null
      this.draft = ''
      this.formStatus = ''
      this.sending = false
      this.needsLogin = !hasAuthToken()
      const id = this.requestId
      if (this.needsLogin || !id) { this.loading = false; return }
      return fetchSupportRequest(id)
          .then((request) => {
            if (generation !== this.generation || id !== this.requestId) return
            if (!request || String(request.id) !== id) throw new Error('SUPPORT_REQUEST_RESPONSE_INVALID')
            this.request = request
          })
          .catch(() => { if (generation === this.generation) this.formStatus = this.$t('intake.portal.chatLoadFail') })
          .finally(() => { if (generation === this.generation) this.loading = false })
    },
    send() {
      const text = (this.draft || '').trim()
      if (!text || !this.canReply || this.sending) return
      this.sending = true
      this.formStatus = ''
      const id = this.requestId
      const generation = this.generation
      return sendSupportRequestMessage(id, text)
          .then((request) => {
            if (generation !== this.generation || id !== this.requestId) return
            if (!request || String(request.id) !== id) throw new Error('SUPPORT_REQUEST_RESPONSE_INVALID')
            this.request = request; this.draft = ''
          })
          .catch(() => { if (generation === this.generation) this.formStatus = this.$t('intake.portal.chatSendFail') })
          .finally(() => { if (generation === this.generation) this.sending = false })
    },
  },
}
</script>
